import {ref, computed, watch} from 'vue'
import {defineStore} from 'pinia'

export const useLocationStore = defineStore('location', () => {
    // State
    const latitude = ref(null)
    const longitude = ref(null)
    const accuracy = ref(null)
    const timestamp = ref(null)
    const error = ref(null)
    const isWatching = ref(false)
    const isUserSelected = ref(false)
    const locationName = ref('')
    const searchHistory = ref([])
    const isLoading = ref(false)
    const user = ref(null)


    // Getters
    const currentPosition = computed(() => {
        if (latitude.value && longitude.value) {
            return {
                latitude: latitude.value,
                longitude: longitude.value,
                accuracy: accuracy.value,
                timestamp: timestamp.value,
                isUserSelected: isUserSelected.value,
                locationName: locationName.value
            }
        }
        return null
    })

    const hasLocation = computed(() =>
        latitude.value !== null && longitude.value !== null
    )

    const formattedAddress = computed(() =>
        locationName.value || 'Location not set'
    )

    // PostGIS parsing function
    const parsePostGISPoint = (ewkbHex) => {
        if (!ewkbHex) return null;
        console.log(ewkbHex)
        try {

            let coords = parseEWKBHex(ewkbHex)
       
            return {
                longitude: coords[0],
                latitude: coords[1]
            };

        } catch (err) {
            console.error('Failed to parse PostGIS point:', err);
            return null;
        }
    };
    const parseEWKBHex = (ewkbHex) => {
        // Skip first 18 characters (SRID and geometry type info)
        const coordsHex = ewkbHex.substring(18);

        // Convert hex to Float64 coordinates
        const getFloat64 = (hex) => {
            const buffer = new ArrayBuffer(8);
            const view = new DataView(buffer);

            for (let i = 0; i < 8; i++) {
                view.setUint8(i, parseInt(hex.substring(i * 2, (i + 1) * 2), 16));
            }

            return view.getFloat64(0, true); // true for little-endian
        };

        const x = getFloat64(coordsHex.substring(0, 16));
        const y = getFloat64(coordsHex.substring(16, 32));

        return [x, y];
    };

    const parseCoordinates = (newLocation) => {
        // Check if newLocation has coordinates property and it's an array
        if (newLocation?.coordinates &&
            Array.isArray(newLocation.coordinates) &&
            newLocation.coordinates.length === 2 &&
            typeof newLocation.coordinates[0] === 'number' &&
            typeof newLocation.coordinates[1] === 'number') {

            // If it matches the [longitude, latitude] format, create coords object
            return {
                latitude: newLocation.coordinates[1],   // Second entry is latitude
                longitude: newLocation.coordinates[0]   // First entry is longitude
            };
        }

        // If not in the expected format, use PostGIS parsing
        return parsePostGISPoint(newLocation);
    };
    // Watch for user location changes
    watch(() => user.value?.location, (newLocation) => {

        if (newLocation) {
            const coords = parseCoordinates(newLocation);

            if (coords) {
                latitude.value = coords.latitude;
                longitude.value = coords.longitude;
                timestamp.value = Date.now();
                updateLocationName(coords.latitude, coords.longitude);
            }
        }
    });

    // Actions
    const updatePosition = (position) => {
        latitude.value = position.coords.latitude
        longitude.value = position.coords.longitude
        accuracy.value = position.coords.accuracy
        timestamp.value = position.timestamp
        isUserSelected.value = false
    }

    const setSelectedLocation = async (lat, lng, name = '') => {
        latitude.value = lat
        longitude.value = lng
        timestamp.value = Date.now()
        isUserSelected.value = true
        error.value = null

        if (name) {
            locationName.value = name
        } else {
            await updateLocationName(lat, lng)
        }

        // Add to search history
        addToSearchHistory({
            lat,
            lng,
            name: locationName.value,
            timestamp: Date.now()
        })
    }

    const saveLocation = async (supabase, userId) => {
        // Format the point as a simple text POINT without ST_GeomFromText
        const pointText = `POINT(${longitude.value} ${latitude.value})`;

        const {data, error} = await supabase
            .from('users')
            .update({
                location: pointText  // Just send the POINT text
            })
            .eq('id', userId)
            .select(`
            id,
            location
        `);

        if (error) {
            console.error('Error saving location:', error);
            return null;
        }

        if (data && data.length > 0) {
            console.log(data[0])
            return data[0];
        }

        return null;
    }

    const getCurrentPosition = async () => {
        if (!navigator.geolocation) {
            error.value = 'Geolocation is not supported by your browser'
            return
        }

        isLoading.value = true
        try {
            const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 5000,
                    maximumAge: 0
                })
            })

            updatePosition(position)
            error.value = null
            await updateLocationName(position.coords.latitude, position.coords.longitude)
        } catch (err) {
            error.value = err.message || 'Failed to get location'
        } finally {
            isLoading.value = false
        }
    }

    const updateLocationName = async (lat, lng) => {
        try {

            const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
            )
            const data = await response.json()
            locationName.value = data.display_name
        } catch (err) {
            console.error('Failed to get location name:', err)
            locationName.value = `${lat.toFixed(6)}, ${lng.toFixed(6)}`
        }
    }

    const startWatching = () => {
        if (!navigator.geolocation) {
            error.value = 'Geolocation is not supported by your browser'
            return
        }

        if (isWatching.value) return

        isWatching.value = true
        const watchId = navigator.geolocation.watchPosition(
            (position) => {
                updatePosition(position)
                error.value = null
            },
            (err) => {
                error.value = err.message
            },
            {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0
            }
        )

        // Store watch ID for cleanup
        return watchId
    }

    const stopWatching = (watchId) => {
        if (watchId) {
            navigator.geolocation.clearWatch(watchId)
        }
        isWatching.value = false
    }

    const searchLocation = async (query) => {
        if (!query) return []

        isLoading.value = true
        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`
            )
            const data = await response.json()
            return data.map(item => ({
                lat: parseFloat(item.lat),
                lng: parseFloat(item.lon),
                name: item.display_name,
                type: item.type,
                importance: item.importance
            }))
        } catch (err) {
            error.value = 'Failed to search location'
            return []
        } finally {
            isLoading.value = false
        }
    }

    const addToSearchHistory = (location) => {
        // Limit history to last 10 searches
        searchHistory.value = [location, ...searchHistory.value.slice(0, 9)]
    }

    const clearSearchHistory = () => {
        searchHistory.value = []
    }

    const resetLocation = () => {
        latitude.value = null
        longitude.value = null
        accuracy.value = null
        timestamp.value = null
        error.value = null
        isUserSelected.value = false
        locationName.value = ''
    }

    const initializeFromStorage = () => {
        const stored = localStorage.getItem('userLocation')
        if (stored) {
            const {lat, lng, name} = JSON.parse(stored)
            setSelectedLocation(lat, lng, name)
        }
    }

    const saveToStorage = () => {
        if (latitude.value && longitude.value) {
            localStorage.setItem('userLocation', JSON.stringify({
                lat: latitude.value,
                lng: longitude.value,
                name: locationName.value
            }))
        }
    }

    return {
        // State
        latitude,
        longitude,
        accuracy,
        timestamp,
        error,
        isWatching,
        isUserSelected,
        locationName,
        searchHistory,
        isLoading,
        user,

        // Getters
        currentPosition,
        hasLocation,
        formattedAddress,

        // Actions
        setSelectedLocation,
        getCurrentPosition,
        startWatching,
        stopWatching,
        searchLocation,
        clearSearchHistory,
        resetLocation,
        initializeFromStorage,
        saveToStorage,
        saveLocation,
        parsePostGISPoint,
        parseEWKBHex
    }
})